import React, { Component, Context } from "react";
import clsx from "clsx";
import orderby from "lodash.orderby";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import ArrowBackOutlinedIcon from "@material-ui/icons/ArrowBackOutlined";
import ArrowForwardIosOutlinedIcon from "@material-ui/icons/ArrowForwardIosOutlined";
import AppContext, { IAppContext } from "../../../contexts/app/AppContext";
import { ClientFirebase } from "../../../laco-common";
import { firewireApp } from "../../../firewire";
import ImageThumbnails from "../../../components/ui/Media/ImageThumbnails";
import { IImage, getFileType } from "../../../models/Image";
import UILoader from "../../../components/ui/UILoader";
import { asyncForEach } from "../../../laco-common/base/utils";
import { withUploadContext, UploadContextProps } from "../../contexts/upload/UploadContext";
import { FileType } from "../../../laco-common/base/models/IFile";
import "./StorageGallery.scss";

export type StorageGalleryPropsBase = {
	path?: string;
	useFirewire?: boolean;
	showFolders?: boolean;
	allFolderName?: string;
	displayRootFolder?: boolean;
	recursive?: boolean;
	isNestedStorageGallery?: boolean;
	selectedImages?: IImage[];
	viewWidth?: number;
	viewHeight?: number;
};

type StorageGalleryProps = StorageGalleryPropsBase & {
	onImageClicked: (image: IImage, i?: number) => any;
} & UploadContextProps;

type StorageGalleryState = {
	loading: boolean;
	selectedPath: string;
	files: IImage[];
	paths: firebase.storage.Reference[];
};

class StorageGallery extends Component<StorageGalleryProps, StorageGalleryState> {
	static contextType: Context<IAppContext> = AppContext;
	private path: string = "";

	state = {
		loading: true,
		selectedPath: this.path,
		files: [],
		paths: [],
	};

	componentDidMount() {
		console.log("StorageGallery.componentDidMount(): props", this.props);
		this.init();
	}

	componentDidUpdate(prevProps: StorageGalleryProps, prevState: StorageGalleryState, snapshot) {
		if (this.props.path !== prevProps.path) {
			this.init();
		}
	}

	init() {
		this.setPath();
		this.loadFiles();
	}

	setPath() {
		this.path = this.props.path || this.props.uploadContext?.path;
		this.setState({ selectedPath: this.path });
	}

	async loadFiles() {
		if (!this.path) return;
		this.setState({ loading: true });
		let clientFirebase: ClientFirebase = this.context.firebase.clientFirebase;
		if (this.props.useFirewire) {
			clientFirebase = firewireApp;
		}
		const storageRef = clientFirebase.getFirebase().storage().ref(this.path);
		const files: IImage[] = [];
		storageRef
			.listAll()
			.then(async (result: firebase.storage.ListResult) => {
				console.log("storage items retrieved total:", result.items?.length);
				await asyncForEach(result.items, async (fileRef: firebase.storage.Reference) => {
					const file = await this.getFile(fileRef, clientFirebase);
					if (file) {
						const { timeCreated, size } = await fileRef.getMetadata();
						files.push({ ...file, size, created: new Date(timeCreated) });
					}
				});
				this.setState({ files: orderby(files, ["created", "size"], ["desc", "asc"]) });
				if (this.props.showFolders || this.props.recursive) {
					const paths = [];
					result.prefixes.forEach((prefix: firebase.storage.Reference) => {
						paths.push(prefix);
					});
					this.setState({ paths });
				}
			})
			.catch((error) => {
				console.error("Failed to load storage path:", this.path, error);
			})
			.finally(() => {
				this.setState({ loading: false });
			});
	}

	async getFile(imageRef: firebase.storage.Reference, clientFirebase: ClientFirebase): Promise<IImage> {
		// filename: string;
		// noFullscreen?: boolean;
		// mediaDate?: number | "string" | any; // string as date format
		// renderSimple?: boolean;
		// fileType
		// filename
		// url
		// thumburl
		// mediaDate
		//     displayDate?: number;
		// mediaDate?: Date;
		//     title?: string;
		// thumbUrl?: string;
		// desc?: string;
		// width?: number;
		// height?: number;

		// const metadata = await imageRef.getMetadata();
		const filename: string = imageRef.name;
		const fileType: FileType = getFileType(filename);
		console.log("fileType:", fileType);
		if (this.props.uploadContext.mediaAccepted) {
			if (this.props.uploadContext.mediaAccepted && !this.props.uploadContext.mediaAccepted.includes(fileType)) {
				return null;
			}
		}
		const url: string =
			clientFirebase.storage._baseUrl + encodeURIComponent(this.path + "/" + filename) + "?alt=media";
		const image: IImage = { filename, fileType, url, title: filename }; // mediaDate: new Date(metadata.timeCreated)
		return image;
	}

	imageClicked(image: IImage) {}

	drillToPath(fullPath: string) {
		this.path = fullPath;
		this.setState({ selectedPath: fullPath });
		this.loadFiles();
	}
	renderPaths() {
		if (!this.props.showFolders) return null;
		return (
			<>
				{!this.props.isNestedStorageGallery &&
					this.props.displayRootFolder &&
					this.path !== this.props.path &&
					this.renderBreadcrumbs()}
				<ul className="paths-list">
					{this.state.paths.map((p: firebase.storage.Reference) => {
						return (
							<li
								key={p.name}
								onClick={() => {
									this.drillToPath(p.fullPath);
								}}
							>
								<FolderOutlinedIcon className="folder-icon" />
								{p.name}
							</li>
						);
					})}
				</ul>
			</>
		);
	}
	renderBreadcrumbs() {
		const path: string = this.path.replace(`${this.props.path}`, "").replace(this.props.path, "").substring(1);
		const parts: string[] = path.split("/");
		if (!parts.length) return;
		return (
			<>
				<ul className="paths-list">
					<li
						className="mb-2"
						onClick={() => {
							this.drillToPath(this.props.path);
						}}
					>
						<ArrowBackOutlinedIcon className="folder-icon" />
						Back to all {this.props.allFolderName || "images"}
					</li>
				</ul>
				<ul className="paths-list">
					{parts.map((p: string, i: number) =>
						i === parts.length - 1 ? (
							parts.length > 1 ? (
								<li key={i} className="unclickable">
									<ArrowForwardIosOutlinedIcon
										style={{ fontSize: "0.65rem", marginLeft: "-9px", marginRight: "6px" }}
									/>
									<FolderOutlinedIcon className="folder-icon" />
									{p}
								</li>
							) : (
								<h3>
									<FolderOutlinedIcon className="folder-icon" /> {p}
								</h3>
							)
						) : (
							<li
								key={i}
								onClick={() => {
									this.drillToPath(`${this.props.path}/${p}`);
								}}
							>
								<FolderOutlinedIcon className="folder-icon" />
								{p}
							</li>
						)
					)}
					{/*<li>{this.path.replace(`${this.props.path}`, "").replace(this.props.path, "").substring(1)}</li>*/}
				</ul>
			</>
		);
	}

	render() {
		if (this.state.loading) return <UILoader display={true} />;
		return (
			<>
				{this.renderPaths()}
				<ImageThumbnails
					images={this.state.files}
					selectedImages={this.props.selectedImages}
					viewWidth={this.props.viewWidth || 200}
					viewHeight={this.props.viewHeight || 150}
					containImage={true}
					displayMetaData
					onClick={(image: IImage, i?: number) => {
						return this.props.onImageClicked ? this.props.onImageClicked(image, i) : null;
					}}
				></ImageThumbnails>
				{this.props.recursive && this.state.paths.length > 0 && (
					<>
						{this.state.paths.map((p: firebase.storage.Reference, i: number) => {
							return (
								<div className="storage-gallery-folder" key={p.fullPath}>
									<h3 className={clsx("folder-name")}>{p.name}</h3>
									<StorageGallery
										{...this.props}
										path={p.fullPath}
										displayRootFolder={false}
										isNestedStorageGallery={true}
									/>
								</div>
							);
						})}
					</>
				)}
			</>
		);
	}
}

export default withUploadContext<StorageGalleryProps>(StorageGallery);
